<script lang="ts">
  import type { ClockCell } from '../admin';
  import ChartTooltip from '@ui/ChartTooltip.svelte';

  // Weekday × hour activity clock (UTC). Members-only — the parent feeds it the
  // mod-excluded `_clock`, and this same component renders the Overview's "Most
  // Active Time" and the Engagement page's "Best time to post" identically.
  let { cells }: { cells: ClockCell[] } = $props();

  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const WD_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const byKey = $derived(new Map(cells.map((c) => [c.weekday * 24 + c.hour, c.count])));
  const max = $derived(Math.max(1, ...cells.map((c) => c.count)));
  const at = (wd: number, hr: number): number => byKey.get(wd * 24 + hr) ?? 0;
  const busiest = $derived(cells.length ? cells.reduce((a, b) => (b.count > a.count ? b : a)) : null);

  // Smooth intensity ramp: empty cells a faint track, everything else primary at an
  // opacity scaled by its share of the peak — a continuous gradient, not hard buckets.
  function cellBg(v: number): string {
    if (v <= 0) return 'color-mix(in oklab, var(--color-base-content) 7%, transparent)';
    return `color-mix(in oklab, var(--color-primary) ${Math.round(16 + (v / max) * 74)}%, transparent)`;
  }
  const label12 = (h: number): string => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;

  let hover = $state<{ wd: number; hr: number; count: number; x: number; y: number } | null>(null);
  function enter(e: MouseEvent, wd: number, hr: number, count: number): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { wd, hr, count, x: r.left + r.width / 2, y: r.top };
  }
</script>

<div class="overflow-x-auto pb-1">
  <div class="grid w-full min-w-[23rem]" style="grid-template-columns: 2.4rem repeat(24, minmax(0,1fr)); gap: 3px;">
    <div></div>
    {#each Array(24) as _, h}
      <div class="pb-0.5 text-center font-mono text-[0.55rem] text-base-content/58">{h % 6 === 0 ? label12(h) : ''}</div>
    {/each}
    {#each WD as d, wd}
      <div class="flex items-center justify-end pr-2 font-mono text-[0.65rem] text-base-content/65">{d}</div>
      {#each Array(24) as _, h}
        {@const v = at(wd, h)}
        {@const isPeak = !!busiest && busiest.weekday === wd && busiest.hour === h}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="h-[17px] rounded-[3px] transition-transform hover:z-10 hover:scale-[1.35] {isPeak ? 'ring-[1.5px] ring-base-content/85' : ''}"
          style:background={cellBg(v)}
          onmouseenter={(e) => enter(e, wd, h, v)}
          onmouseleave={() => (hover = null)}
        ></div>
      {/each}
    {/each}
  </div>
</div>

<div class="mt-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
  {#if busiest}
    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span class="eyebrow text-[0.6rem] text-base-content/65">Peak</span>
      <span class="font-display text-xl font-bold tracking-tight">{WD[busiest.weekday]} {String(busiest.hour).padStart(2, '0')}:00</span>
      <span class="font-mono text-xs text-base-content/65">UTC · {busiest.count.toLocaleString()} events</span>
    </div>
  {/if}
  <div class="flex items-center gap-1.5 font-mono text-[0.6rem] text-base-content/62">
    <span>less</span>
    <span class="h-2 w-16 rounded-full" style="background:linear-gradient(to right, color-mix(in oklab, var(--color-primary) 16%, transparent), var(--color-primary))"></span>
    <span>more</span>
  </div>
</div>

{#if hover}
  <ChartTooltip x={hover.x} y={hover.y}>
    <div class="font-mono text-[0.7rem] font-semibold">{WD_FULL[hover.wd]} · {String(hover.hr).padStart(2, '0')}:00 UTC</div>
    <div class="mt-0.5 text-xs text-base-content/70">
      <span class="font-semibold">{hover.count.toLocaleString()}</span> {hover.count === 1 ? 'event' : 'events'}
      {#if hover.count > 0 && busiest}<span class="text-base-content/65"> · {Math.round((hover.count / busiest.count) * 100)}% of peak</span>{/if}
    </div>
  </ChartTooltip>
{/if}
